import { motion } from 'framer-motion';
import { HiArrowRight, HiSparkles } from 'react-icons/hi';

const HeroSectionV2 = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      data-testid="hero-section-v2"
      className="min-h-screen pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-white via-[#E8F4F8] to-[#F0F7F4] relative overflow-hidden"
    >
      {/* Animated decorative shapes */}
      <motion.div 
        animate={{ 
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-[#5B8FB9]/20 to-[#7CB98F]/20 rounded-full blur-3xl" 
      />
      <motion.div 
        animate={{ 
          scale: [1.2, 1, 1.2],
          x: [0, 50, 0]
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-[#D4A5B5]/30 to-[#5B8FB9]/20 rounded-full blur-3xl" 
      />
      <motion.div 
        animate={{ y: [0, -30, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/3 right-1/4 w-32 h-32 bg-[#7CB98F]/20 rounded-full blur-2xl" 
      />
      <motion.div 
        animate={{ y: [0, 20, 0], x: [0, -20, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-1/3 left-1/4 w-24 h-24 bg-[#D4A5B5]/30 rounded-full blur-xl" 
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[80vh]">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="order-2 lg:order-1"
          >
            <motion.span 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#5B8FB9]/20 to-[#7CB98F]/20 text-[#5B8FB9] text-xs font-bold uppercase tracking-[0.2em] rounded-full mb-6 border border-[#5B8FB9]/20"
            >
              <HiSparkles className="w-4 h-4" />
              Leadership & Workplace Well-being Advisor
            </motion.span>
            
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[1.1] text-gray-900 mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="hero-headline-v2"
            >
              Build High-Performing Teams{' '}
              <span className="bg-gradient-to-r from-[#5B8FB9] via-[#7CB98F] to-[#D4A5B5] bg-clip-text text-transparent italic">Without Burnout</span>
            </h1>
            
            <p className="text-lg sm:text-xl font-light leading-relaxed text-gray-600 mb-10 max-w-xl" data-testid="hero-subheadline-v2">
              I help leaders and organizations create sustainable performance through 
              conscious leadership and workplace well-being strategies.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => scrollToSection('#contact')}
                data-testid="hero-cta-primary-v2"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#5B8FB9] via-[#7CB98F] to-[#5B8FB9] bg-[length:200%_100%] text-white font-medium rounded-full transition-all duration-500 hover:bg-right shadow-lg shadow-[#5B8FB9]/20"
              >
                Book a Discovery Call
                <HiArrowRight className="w-5 h-5" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => scrollToSection('#services')}
                data-testid="hero-cta-secondary-v2"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/80 backdrop-blur border-2 border-[#7CB98F] text-[#5B8FB9] font-medium rounded-full transition-all duration-300 hover:bg-[#7CB98F]/10 hover:border-[#5B8FB9]"
              >
                Explore Services
              </motion.button>
            </div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="order-1 lg:order-2 relative flex justify-center lg:justify-end"
          >
            <div className="relative">
              {/* Animated background shapes */}
              <motion.div 
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 bg-gradient-to-br from-[#5B8FB9]/30 via-[#7CB98F]/20 to-[#D4A5B5]/30 rounded-[60%_40%_50%_50%] transform scale-110" 
              />
              
              {/* Decorative circles */}
              <motion.div 
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-6 -left-6 w-20 h-20 bg-gradient-to-br from-[#5B8FB9] to-[#7CB98F] rounded-full opacity-60" 
              />
              <motion.div 
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-4 -right-4 w-16 h-16 bg-gradient-to-br from-[#D4A5B5] to-[#5B8FB9] rounded-full opacity-70" 
              />
              <motion.div 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-1/2 -right-8 w-12 h-12 bg-[#7CB98F] rounded-full opacity-50" 
              />
              
              {/* Portrait image in oval */}
              <div className="relative w-80 h-96 lg:w-96 lg:h-[28rem] rounded-[50%_50%_45%_45%] overflow-hidden border-4 border-white shadow-2xl shadow-[#5B8FB9]/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_leadership-growth-4/artifacts/l0as65vj_Susana%20image.jpeg"
                  alt="Susana - Leadership Advisor"
                  className="w-full h-full object-cover object-top"
                  data-testid="hero-portrait-v2"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-6 h-10 border-2 border-[#5B8FB9]/30 rounded-full flex justify-center pt-2"
        >
          <motion.div 
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-1.5 h-1.5 bg-gradient-to-b from-[#5B8FB9] to-[#7CB98F] rounded-full" 
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSectionV2;
